import { GridOff, GridOn } from '@mui/icons-material'
import {
  Box,
  Card,
  Chip,
  Grid2,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'

import ImageWithBoundingBoxes from '@/components/bounding-box/ImageWithBoundingBoxes'
import { ProductInitialInfo } from '@/components/ProductDetailsCard'
import { DisplaySnapshotWithImageBoxesFragment, ProcessStatus } from '@/graphql'
import useT from '@/i18n/useT'
import { getFullSizeImage } from '@/utils/image.helpers'
import { ProductHelpers } from '@/utils/products.helpers'

const RestockSnapshotCard = ({
  snapshot,
  productInfoByUpc,
}: {
  snapshot: DisplaySnapshotWithImageBoxesFragment | null
  productInfoByUpc?: Record<string, ProductInitialInfo>
}) => {
  const { t } = useT()
  const [showBoxes, setShowBoxes] = useState<boolean>(true)
  if (!snapshot || !snapshot.images || !snapshot.images[0]?.url) {
    return null
  }
  return (
    <Grid2 size={{ xs: 12, md: 6 }}>
      <Card
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Chip
              label={
                snapshot.type === 'pre_restock'
                  ? t('restockSnapshotCard.before')
                  : t('restockSnapshotCard.after')
              }
              size="small"
              color={snapshot.type === 'pre_restock' ? 'error' : 'success'}
              sx={{
                fontWeight: 'medium',
                fontSize: '0.75rem',
              }}
            />
            <Tooltip
              title={
                showBoxes
                  ? t('restockSnapshotCard.tooltip.hideBoundingBoxes')
                  : t('restockSnapshotCard.tooltip.showBoundingBoxes')
              }
            >
              <IconButton onClick={() => setShowBoxes(!showBoxes)}>
                {showBoxes ? <GridOff /> : <GridOn />}
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="caption">
            {snapshot.captureTime
              ? t('restockSnapshotCard.captureTime', {
                  date: dayjs(snapshot.captureTime).toDate(),
                })
              : t('restockSnapshotCard.noTimestamp')}
          </Typography>
        </Box>
        <ImageWithBoundingBoxes
          showBoxes={showBoxes}
          style={{ width: '100%' }}
          imageStyle={{
            borderRadius: '8px',
            width: '100%',
            maxHeight: undefined,
          }}
          status={snapshot.images[0].process?.status || ProcessStatus.PENDING}
          errorMessage={snapshot.images[0].process?.error}
          source={getFullSizeImage(snapshot.images[0].url)}
          boxes={ProductHelpers.filterRestockBoxes(
            snapshot.images[0]?.boxSet?.boundingBoxes || [],
          ).map(box => ({
            ...box,
            color: ProductHelpers.getBoundingBoxColor(box.classifiedLabel),
          }))}
          productInfoByUpc={productInfoByUpc}
        />
      </Card>
    </Grid2>
  )
}

export default RestockSnapshotCard
